import { useCallback, useEffect, useMemo, useState } from 'react';
import ValidationStats from './ValidationStats';
import ValidationFilters from './ValidationFilters';
import ValidationTable from './ValidationTable';
import ValidationDetailModal from './ValidationDetailModal';
import Layout from '../Shared/Layout';
import HeaderValidation from './HeaderValidation';
import { validationService } from '../service/api/validationService';
import { toast } from 'sonner';
import { useUserData } from '../service/Hooks/useUserData';
import { useLoader } from '../service/Hooks/useLoader';
import LoadingSpinner from './ LoadingSpinner';

const ValidationDashboard = () => {
  // État pour toutes les validations (données brutes du backend)
  const [allValidations, setAllValidations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedValidation, setSelectedValidation] = useState(null);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const { accountId } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  
  // État pour les filtres côté client
  const [filters, setFilters] = useState({
    Status: '',
    EntityName: '',
    SearchTerm: ''
  });

  // État pour la pagination côté client
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // ========== FILTRAGE CÔTÉ CLIENT ==========
  const filteredValidations = useMemo(() => {
    let result = [...allValidations];

    // Filtre par statut
    if (filters.Status) {
      result = result.filter(v => v.status === filters.Status);
    }

    // Filtre par type d'entité
    if (filters.EntityName) {
      result = result.filter(v => v.entityName === filters.EntityName);
    }

    // Filtre par recherche (insensible à la casse)
    if (filters.SearchTerm) {
      const searchLower = filters.SearchTerm.toLowerCase().trim();
      result = result.filter(v => {
        // Recherche dans plusieurs champs
        const searchableFields = [
          v.validationId?.toString(),
          v.entityId?.toString(),
          v.orderReference,
          v.createdByUserName,
          v.entityName,
          v.status
        ];

        return searchableFields.some(field => 
          field?.toString().toLowerCase().includes(searchLower)
        );
      });
    }

    return result;
  }, [allValidations, filters]);

  // ========== PAGINATION CÔTÉ CLIENT ==========
  const paginatedValidations = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredValidations.slice(startIndex, endIndex);
  }, [filteredValidations, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredValidations.length / pageSize);
  const totalCount = filteredValidations.length;

  // ========== CHARGEMENT DES DONNÉES ==========
  const loadValidationTasks = useCallback(async () => {
    if (!accountId) return;
    
    try {
      setLoading(true);
      setError(null);

      // Charger TOUTES les validations sans filtres backend
      const apiFilters = { 
        AccountId: accountId,
        PageNumber: 1,
        PageSize: 1000 // Charger toutes les données
      };
      
      const result = await validationService.getMyValidationTasks(apiFilters);

      if (result.success) {
        setAllValidations(result.data.items || []);
      } else {
        setError(result.error || 'Erreur lors du chargement');
        toast.error(result.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError('Erreur lors du chargement des validations');
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
      setNeedsRefresh(false);
    }
  }, [accountId]);

  // ========== EFFECTS ==========
  useEffect(() => {
    if (accountId) {
      loadValidationTasks();
    }
  }, [accountId, loadValidationTasks]);

  useEffect(() => {
    if (needsRefresh && accountId) {
      loadValidationTasks();
    }
  }, [needsRefresh, accountId, loadValidationTasks]);

  // Reset page à 1 quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // ========== HANDLERS ==========
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    // Scroll vers le haut de la table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleApprove = useCallback(async (entityType, entityId, comments) => {
    if (!accountId) {
      toast.error('Utilisateur non authentifié');
      return;
    }

    if (!entityType || !entityId) {
      toast.error('Données de validation incomplètes');
      return;
    }

    try {
      showLoader({ message: "Approbation en cours...", indeterminate: true });
      
      const result = await validationService.approveValidation(
        entityType, 
        entityId, 
        accountId, 
        comments
      );

      if (result.success) {
        toast.success('Validation approuvée avec succès');
        setNeedsRefresh(true);
      } else {
        toast.error(result.error || "Erreur lors de l'approbation");
      }
    } catch (error) {
      toast.error(error.message || "Une erreur inattendue est survenue");
    } finally {
      setSelectedValidation(null);
      hideLoader();
    }
  }, [accountId, showLoader, hideLoader]);

  const handleReject = useCallback(async (validationId, comments) => {
    if (!accountId) {
      toast.error('Utilisateur non authentifié');
      return;
    }

    try {
      showLoader({ message: "Rejet en cours...", indeterminate: true });
      await validationService.rejectValidation(validationId, accountId, comments);
      toast.success('Validation rejetée avec succès');
      setNeedsRefresh(true);
    } catch (error) {
      toast.error(error.message || "Une erreur inattendue est survenue");
    } finally {
      setSelectedValidation(null);
      hideLoader();
    }
  }, [accountId, showLoader, hideLoader]);

  const handleManualRefresh = useCallback(() => {
    setNeedsRefresh(true);
    setCurrentPage(1);
  }, []);

  // ========== MODAL OPTIMISÉE ==========
  const modalContent = useMemo(() => {
    if (!selectedValidation) return null;
    
    return (
      <ValidationDetailModal
        validation={selectedValidation}
        entityId={selectedValidation.entityId}
        entityType={selectedValidation.entityName}
        onClose={() => setSelectedValidation(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        key={`${selectedValidation.entityId}-${selectedValidation.entityName}`}
      />
    );
  }, [selectedValidation, handleApprove, handleReject]);

  // ========== LOADING STATE ==========
  if (loading && allValidations.length === 0) {
    return (
      <Layout>
        <div className="content-body default-height">
          <div className="container-fluid">
            <HeaderValidation />
            <LoadingSpinner message="Chargement des validations..." />
          </div>
        </div>
      </Layout>
    );
  }

  // ========== RENDER ==========
  return (
    <Layout>
      <div className="content-body default-height">
        <div className="container-fluid">
          <HeaderValidation onRefresh={handleManualRefresh} />
          
          {error && <div className="alert alert-danger"><strong>Erreur :</strong> {error}</div>}
          
          <div className="row">
            <div className="col-12">
              {/* Stats : Le skeleton est géré à l'intérieur du composant via useValidationStats */}
              <ValidationStats validations={allValidations} />
              
              <ValidationFilters 
                filters={filters} 
                onFiltersChange={handleFiltersChange}
              />

              {/* Table : On lui passe l'état "loading". 
                  Elle affichera le TableSkeleton au lieu du Spinner. */}
              <ValidationTable
                validations={paginatedValidations}
                onSelectValidation={setSelectedValidation}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                loading={loading} 
              />
            </div>
          </div>

          {modalContent}
        </div>
      </div>
    </Layout>
  );
};

export default ValidationDashboard;